/**
 * useCrudForm.js
 *
 * Hook centralizado para gerenciamento de formulário CRUD.
 * Gerencia carregamento, estado, validação e salvamento de itens.
 */

import { useState, useCallback, useMemo } from 'react';
import { useEffectAsync } from '../../reactHelper';
import { useTranslation } from '../components/LocalizationProvider';
import fetchOrThrow from '../util/fetchOrThrow';

/**
 * Obtém valor aninhado de um objeto usando dot notation
 * Ex: getNestedValue(obj, 'attributes.form.plate')
 *
 * @param {Object} obj - Objeto fonte
 * @param {string} path - Caminho com dots
 * @returns {any} Valor encontrado ou undefined
 */
export const getNestedValue = (obj, path) => {
  if (!obj || !path) return undefined;

  return path.split('.').reduce((acc, part) => {
    if (acc === undefined || acc === null) return undefined;
    return acc[part];
  }, obj);
};

/**
 * Define valor aninhado em um objeto usando dot notation
 * Ex: setNestedValue(obj, 'attributes.form.plate', 'ABC-1234')
 *
 * @param {Object} obj - Objeto a modificar (não muta, retorna novo)
 * @param {string} path - Caminho com dots
 * @param {any} value - Valor a definir
 * @returns {Object} Novo objeto com valor definido
 */
export const setNestedValue = (obj, path, value) => {
  if (!path) return obj;

  const parts = path.split('.');
  const result = { ...obj };

  let current = result;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const part = parts[i];
    current[part] = current[part] ? { ...current[part] } : {};
    current = current[part];
  }

  current[parts[parts.length - 1]] = value;
  return result;
};

/**
 * Garante que a estrutura de formulário existe
 * Cria attributes.form se não existir
 *
 * @param {Object} item - Item a processar
 * @returns {Object} Item com estrutura garantida
 */
export const ensureFormStructure = (item) => {
  if (!item) return item;

  return {
    ...item,
    attributes: {
      ...item.attributes,
      form: {
        ...(item.attributes?.form || {}),
      },
    },
  };
};

/**
 * Hook principal para gerenciamento de CRUD
 *
 * @param {Object} options
 * @param {string} options.endpoint - API endpoint (ex: 'devices')
 * @param {Object} options.schema - Schema de validação (opcional)
 * @param {string|number} options.id - ID do item (undefined para novo)
 * @param {Object} options.defaultItem - Item padrão para novo cadastro
 * @param {Array<string>} options.requiredFields - Campos obrigatórios
 * @param {Function} options.customValidate - Validação customizada
 * @param {Function} options.onSaveSuccess - Callback de sucesso
 * @param {Function} options.onSaveError - Callback de erro
 */
const useCrudForm = ({
  endpoint,
  schema,
  id,
  defaultItem = {},
  requiredFields,
  customValidate,
  onSaveSuccess,
  onSaveError,
}) => {
  const t = useTranslation();

  // Estado principal
  const [item, setItem] = useState(null);
  const [originalItem, setOriginalItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Campos obrigatórios (do schema ou props)
  const effectiveRequiredFields = useMemo(
    () => requiredFields || schema?.requiredFields || [],
    [requiredFields, schema],
  );

  // Carregar item
  useEffectAsync(async () => {
    setLoading(true);
    try {
      if (id) {
        const response = await fetchOrThrow(`/api/${endpoint}/${id}`);
        const data = await response.json();
        const structured = ensureFormStructure(data);
        setItem(structured);
        setOriginalItem(structured);
      } else {
        const structured = ensureFormStructure({ ...defaultItem });
        setItem(structured);
        setOriginalItem(null);
      }
    } catch (error) {
      setErrors({ _form: error.message });
    } finally {
      setLoading(false);
    }
  }, [id, endpoint]);

  // Verifica se o item foi modificado
  const isDirty = useMemo(() => {
    if (!item) return false;
    if (!originalItem) return true; // Novo item sempre é dirty

    return JSON.stringify(item) !== JSON.stringify(originalItem);
  }, [item, originalItem]);

  // Validação
  const validate = useCallback(() => {
    if (!item) return false;

    const fieldErrors = {};

    // Validação de campos obrigatórios
    effectiveRequiredFields.forEach((field) => {
      const value = getNestedValue(item, field);
      if (value === undefined || value === null || value === '') {
        fieldErrors[field] = t('sharedRequired');
      }
    });

    // Validação customizada do schema
    if (schema?.validate) {
      const customErrors = schema.validate(item, t);
      if (customErrors) {
        Object.assign(fieldErrors, customErrors);
      }
    }

    // Validação customizada via prop
    if (customValidate) {
      const customErrors = customValidate(item, t);
      if (customErrors) {
        Object.assign(fieldErrors, customErrors);
      }
    }

    setErrors(fieldErrors);
    return Object.keys(fieldErrors).length === 0;
  }, [item, effectiveRequiredFields, schema, customValidate, t]);

  // Update field (suporta dot notation)
  const updateField = useCallback((path, value) => {
    setItem((prev) => setNestedValue({ ...prev }, path, value));
    // Limpa erro do campo ao editar
    setErrors((prev) => {
      const next = { ...prev };
      delete next[path];
      delete next._form; // Limpa erro geral também
      return next;
    });
  }, []);

  // Update form field (atalho para attributes.form.*)
  const updateFormField = useCallback((key, value) => {
    updateField(`attributes.form.${key}`, value);
  }, [updateField]);

  // Update múltiplos campos de uma vez
  const updateFields = useCallback((updates) => {
    setItem((prev) => {
      let result = { ...prev };
      Object.entries(updates).forEach(([path, value]) => {
        result = setNestedValue(result, path, value);
      });
      return result;
    });
    // Limpa erros dos campos atualizados
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(updates).forEach((path) => {
        delete next[path];
      });
      delete next._form;
      return next;
    });
  }, []);

  // Reset para valores originais
  const resetItem = useCallback(() => {
    if (originalItem) {
      setItem(ensureFormStructure({ ...originalItem }));
    } else {
      setItem(ensureFormStructure({ ...defaultItem }));
    }
    setErrors({});
  }, [originalItem, defaultItem]);

  // Obter erro de um campo específico
  const getFieldError = useCallback((path) => errors[path], [errors]);

  // Verificar se campo tem erro
  const hasFieldError = useCallback((path) => !!errors[path], [errors]);

  // Salvar
  const handleSave = useCallback(async () => {
    if (!validate()) return false;

    setSaving(true);
    setErrors({});

    try {
      const url = id ? `/api/${endpoint}/${id}` : `/api/${endpoint}`;
      const method = id ? 'PUT' : 'POST';

      const response = await fetchOrThrow(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item),
      });

      const savedItem = await response.json();

      // Atualiza original para refletir o estado salvo
      setOriginalItem(ensureFormStructure(savedItem));
      setItem(ensureFormStructure(savedItem));

      if (onSaveSuccess) {
        onSaveSuccess(savedItem);
      }

      return savedItem;
    } catch (error) {
      const errorMessage = error.message || t('errorGeneral');
      setErrors({ _form: errorMessage });

      if (onSaveError) {
        onSaveError(error);
      }

      return false;
    } finally {
      setSaving(false);
    }
  }, [item, id, endpoint, validate, onSaveSuccess, onSaveError, t]);

  // Deletar
  const handleDelete = useCallback(async () => {
    if (!id) return false;

    setSaving(true);
    setErrors({});

    try {
      await fetchOrThrow(`/api/${endpoint}/${id}`, {
        method: 'DELETE',
      });

      return true;
    } catch (error) {
      const errorMessage = error.message || t('errorGeneral');
      setErrors({ _form: errorMessage });
      return false;
    } finally {
      setSaving(false);
    }
  }, [id, endpoint, t]);

  return {
    // Estado
    item,
    setItem,
    loading,
    saving,
    errors,
    isDirty,

    // Validação
    validate,
    getFieldError,
    hasFieldError,

    // Ações
    updateField,
    updateFormField,
    updateFields,
    resetItem,
    handleSave,
    handleDelete,

    // Helpers
    getNestedValue: (path) => getNestedValue(item, path),
    isNew: !id,
  };
};

export default useCrudForm;
